import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { pulse } from "@/utils/pulse";
import { trackVisitorCadence } from "@/utils/pulse-identity";
import { createDwellTracker } from "@/utils/pulse-dwell";
import { trackRsvpAudience } from "@/utils/pulse-rsvp-audience";

const HEARTBEAT_PIXEL_URL =
  "https://etrspsphgbkapczqxboy.supabase.co/functions/v1/logo-transparent";

declare global {
  interface Window {
    __heartbeatFired?: boolean;
  }
}

function onIdle(cb: () => void) {
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (typeof ric === "function") ric(cb, { timeout: 2000 });
  else setTimeout(cb, 0);
}

function fireHeartbeatOnce() {
  if (typeof window === "undefined" || window.__heartbeatFired) return;
  window.__heartbeatFired = true;
  try {
    const img = new Image();
    img.referrerPolicy = "no-referrer-when-downgrade";
    img.src = HEARTBEAT_PIXEL_URL + "?t=" + Date.now();
  } catch { /* noop */ }
}

/**
 * Fires a Pulse `pageview` event on every client-side route change.
 * Also fires a one-shot anonymous heartbeat pixel and privacy-preserving
 * visitor cadence events on first mount. All non-critical work is deferred
 * to requestIdleCallback so it never competes with first paint.
 */
export function PulsePageview() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const dwell = useRef<ReturnType<typeof createDwellTracker> | null>(null);

  useEffect(() => {
    onIdle(() => {
      fireHeartbeatOnce();
      trackVisitorCadence();
    });
  }, []);

  // Candidate-surface dwell: visibility-aware, reported on leave. The
  // pagehide flush is the only listener that must NOT defer to idle —
  // it is the last tick before the page dies.
  useEffect(() => {
    dwell.current = createDwellTracker();
    const onVisibility = () => dwell.current?.visibilityChanged();
    const onPageHide = () => dwell.current?.pageHide();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      dwell.current?.pageHide();
      dwell.current = null;
    };
  }, []);

  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    pulse("pageview", location.pathname);
    dwell.current?.enter(location.pathname);
    onIdle(() => trackRsvpAudience(location.pathname));
  }, [location.pathname, location.search]);

  return null;
}
